import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { 
    Building, User, Check, Upload, Sparkles, ArrowRight, Camera, Palette 
} from 'lucide-react';
import { baseUrl } from '../config';

/**
 * INLINE Community Creator Component
 * Auto-detects Production House and pre-fills form
 * Shows WITHIN page body, NOT as popup
 */
const CommunityCreator = ({ onSuccess, onCancel }) => {
    const [step, setStep] = useState(1); // 1: Detection, 2: Form
    const [loading, setLoading] = useState(false);
    const [productionHouse, setProductionHouse] = useState(null);
    const [useProductionHouse, setUseProductionHouse] = useState(false);
    const [logoPreview, setLogoPreview] = useState(null);
    
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        community_type: 'public',
        logo_file: null,
        primary_color: '#0d9488',
        secondary_color: '#ffffff',
        accent_color: '#000000'
    });

    // Detect Production House on mount
    useEffect(() => {
        detectProductionHouse();
    }, []);

    const detectProductionHouse = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("access_token"); // ✅ CORRECT
            
            const response = await fetch(`${baseUrl}/getProductionHouseByOwner`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                setProductionHouse(data);
                setStep(1); // Show detection step
            } else {
                setProductionHouse(null);
                setStep(2); // Skip to form
            }
        } catch (error) {
            setProductionHouse(null);
            setStep(2);
        } finally {
            setLoading(false);
        }
    };

    const handleUseProductionHouse = () => {
        setUseProductionHouse(true);
        
        // Pre-fill form with Production House data
        setFormData({
            ...formData,
            name: productionHouse.company_name,
            description: productionHouse.company_description || ''
        });
        
        // Set logo preview
        if (productionHouse.company_logo) {
            setLogoPreview(`${baseUrl}/img/${productionHouse.company_logo}`);
        }
        
        setStep(2);
    };

    const handleManualCreation = () => {
        setUseProductionHouse(false);
        setStep(2);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                alert('Logo file is too large. Maximum size is 5MB.');
                return;
            }
            
            setFormData(prev => ({ ...prev, logo_file: file }));
            
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name.trim()) {
            alert('Community name is required');
            return;
        }
        
        try {
            setLoading(true);
            const token = localStorage.getItem("access_token"); // ✅ CORRECT
            
            let logoUrl = null;
            
            // Upload logo if user selected new one
            if (formData.logo_file) {
                const uploadFormData = new FormData();
                uploadFormData.append('file', formData.logo_file);
                uploadFormData.append('file_type', 'image');
                uploadFormData.append('user_id', 0);
                
                const uploadResponse = await fetch(`${baseUrl}/upload/direct`, {
                    method: 'POST',
                    body: uploadFormData
                });
                
                if (uploadResponse.ok) {
                    const uploadData = await uploadResponse.json();
                    logoUrl = uploadData.public_url;
                }
            } 
            // Use Production House logo if using PH and no new logo
            else if (useProductionHouse && productionHouse.company_logo) {
                logoUrl = `${baseUrl}/img/${productionHouse.company_logo}`;
            }
            
            const communityData = {
                name: formData.name,
                description: formData.description,
                community_type: formData.community_type,
                logo_url: logoUrl,
                theme_colors: {
                    primary: formData.primary_color,
                    secondary: formData.secondary_color,
                    accent: formData.accent_color
                }
            };
            
            const response = await fetch(`${baseUrl}/community/create`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(communityData)
            });
            
            if (response.ok) {
                const result = await response.json();
                onSuccess(result);
            } else {
                const error = await response.json();
                alert(error.detail || 'Failed to create community');
            }
        } catch (error) {
            alert('Failed to create community. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-6 sm:p-8 my-8">
            {loading && step === 1 ? (
                // Loading state
                <div className="text-center py-16">
                    <div className="w-12 h-12 border-4 border-gray-200 border-t-teal-600 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Detecting your production house...</p>
                </div>
            ) : step === 1 && productionHouse ? (
                // Step 1: Production House Detected
                <div>
                    <div className="text-center mb-10">
                        <Sparkles className="h-10 w-10 text-teal-600 mx-auto mb-5" />
                        <h2 className="text-2xl sm:text-3xl font-bold text-black mb-3">We Found Your Production House!</h2>
                        <p className="text-gray-600 text-sm sm:text-base">Would you like to create a community using your production house details?</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Use Production House */}
                        <div 
                            onClick={handleUseProductionHouse}
                            className="border-2 border-gray-200 rounded-xl p-6 sm:p-8 cursor-pointer transition-all duration-300 hover:border-teal-600 hover:shadow-xl hover:bg-gradient-to-br hover:from-teal-50 hover:to-cyan-50 text-center"
                        >
                            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-5">
                                <Building className="h-8 w-8 sm:h-10 sm:w-10 text-teal-600" />
                            </div>
                            <h3 className="text-lg sm:text-xl font-semibold text-black mb-4">Use Production House</h3>
                            <div className="bg-white rounded-lg p-3 sm:p-4 mb-5 flex items-center gap-3 sm:gap-4 text-left shadow-sm">
                                {productionHouse.company_logo ? (
                                    <img 
                                        src={`${baseUrl}/img/${productionHouse.company_logo}`}
                                        alt={productionHouse.company_name}
                                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover flex-shrink-0"
                                    />
                                ) : (
                                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-teal-600 text-white flex items-center justify-center text-xl sm:text-2xl font-bold flex-shrink-0">
                                        {productionHouse.company_name.charAt(0)}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-black text-sm sm:text-base truncate">{productionHouse.company_name}</p>
                                    <p className="text-xs text-gray-500 truncate">{productionHouse.company_description || 'Production House'}</p>
                                </div>
                            </div>
                            <button className="bg-teal-600 hover:bg-teal-700 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold inline-flex items-center text-sm sm:text-base transition-all">
                                Select <ArrowRight className="h-4 w-4 ml-2" />
                            </button>
                        </div>

                        {/* Create Manually */}
                        <div 
                            onClick={handleManualCreation}
                            className="border-2 border-gray-200 rounded-xl p-6 sm:p-8 cursor-pointer transition-all duration-300 hover:border-teal-600 hover:shadow-xl hover:bg-gradient-to-br hover:from-gray-50 hover:to-gray-100 text-center"
                        >
                            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-5">
                                <User className="h-8 w-8 sm:h-10 sm:w-10 text-gray-700" />
                            </div>
                            <h3 className="text-lg sm:text-xl font-semibold text-black mb-4">Create Manually</h3>
                            <p className="text-gray-600 text-xs sm:text-sm mb-6">Start from scratch with your own details</p>
                            <button className="bg-gray-800 hover:bg-black text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold inline-flex items-center text-sm sm:text-base transition-all">
                                Create New <ArrowRight className="h-4 w-4 ml-2" />
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                // Step 2: Form
                <div>
                    <div className="text-center mb-8">
                        <h2 className="text-2xl sm:text-3xl font-bold text-black mb-3 flex items-center justify-center gap-2">
                            {useProductionHouse ? (
                                <>
                                    <Building className="h-6 w-6 sm:h-7 sm:w-7" />
                                    <span>Create from Production House</span>
                                </>
                            ) : (
                                <>
                                    <Sparkles className="h-6 w-6 sm:h-7 sm:w-7" />
                                    <span>Create Your Community</span>
                                </>
                            )}
                        </h2>
                        <p className="text-gray-600 text-sm sm:text-base">Build your creative space and connect with filmmakers</p>
                    </div>

                    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
                        {/* Logo Upload */}
                        <div className="mb-6">
                            <label className="block font-semibold text-black mb-3 text-sm">Community Logo</label>
                            <div className="flex justify-center">
                                {logoPreview ? (
                                    <div className="relative w-32 h-32 sm:w-36 sm:h-36">
                                        <img src={logoPreview} alt="Logo" className="w-full h-full object-cover rounded-xl border-2 border-gray-200" />
                                        <button 
                                            type="button"
                                            onClick={() => document.getElementById('logo-input').click()}
                                            className="absolute bottom-2 right-2 w-8 h-8 sm:w-9 sm:h-9 bg-teal-600 hover:bg-teal-700 border-2 border-white rounded-full flex items-center justify-center transition-all hover:scale-110"
                                        >
                                            <Camera className="h-4 w-4 text-white" />
                                        </button>
                                    </div>
                                ) : (
                                    <div 
                                        onClick={() => document.getElementById('logo-input').click()}
                                        className="w-32 h-32 sm:w-36 sm:h-36 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer transition-all hover:border-teal-600 hover:bg-teal-50 bg-gray-50"
                                    >
                                        <Upload className="h-7 w-7 sm:h-8 sm:w-8 text-gray-400 mb-2" />
                                        <p className="font-semibold text-black text-xs sm:text-sm">Upload Logo</p>
                                        <span className="text-[10px] sm:text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</span>
                                    </div>
                                )}
                                <input
                                    id="logo-input"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleLogoChange}
                                    className="hidden"
                                />
                            </div>
                        </div>

                        {/* Name */}
                        <div className="mb-6">
                            <label htmlFor="name" className="block font-semibold text-black mb-2 text-sm">Community Name *</label>
                            <input
                                id="name"
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Enter community name"
                                required
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm sm:text-base focus:outline-none focus:border-teal-600 focus:ring-4 focus:ring-teal-100 transition-all"
                            />
                        </div>

                        {/* Description */}
                        <div className="mb-6">
                            <label htmlFor="description" className="block font-semibold text-black mb-2 text-sm">Description</label>
                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Tell people what your community is about..."
                                rows="4"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm sm:text-base resize-y focus:outline-none focus:border-teal-600 focus:ring-4 focus:ring-teal-100 transition-all"
                            />
                        </div>

                        {/* Privacy */}
                        <div className="mb-6">
                            <label htmlFor="type" className="block font-semibold text-black mb-2 text-sm">Privacy</label>
                            <select
                                id="type"
                                name="community_type"
                                value={formData.community_type}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm sm:text-base focus:outline-none focus:border-teal-600 focus:ring-4 focus:ring-teal-100 transition-all"
                            >
                                <option value="public">Public - Anyone can join</option>
                                <option value="private">Private - Invitation only</option>
                            </select>
                        </div>

                        {/* Theme Colors */}
                        <div className="mb-8">
                            <label className="block font-semibold text-black mb-3 text-sm flex items-center gap-2">
                                <Palette className="h-4 w-4" />
                                Theme Colors
                            </label>
                            <div className="grid grid-cols-3 gap-3 sm:gap-4">
                                {['primary', 'secondary', 'accent'].map((colorType) => (
                                    <div key={colorType} className="flex flex-col gap-2">
                                        <label htmlFor={`${colorType}-color`} className="text-xs font-medium text-gray-600 capitalize">{colorType}</label>
                                        <input
                                            id={`${colorType}-color`}
                                            type="color"
                                            name={`${colorType}_color`}
                                            value={formData[`${colorType}_color`]}
                                            onChange={handleChange}
                                            className="w-full h-10 sm:h-12 border-2 border-gray-200 rounded-lg cursor-pointer transition-all hover:border-teal-600"
                                        />
                                        <span className="text-[10px] sm:text-xs text-gray-500 text-center font-mono">{formData[`${colorType}_color`]}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
                            {onCancel && (
                                <button 
                                    type="button"
                                    onClick={onCancel}
                                    disabled={loading}
                                    className="w-full sm:flex-1 px-6 py-3 bg-white border-2 border-gray-200 rounded-lg font-semibold text-black text-sm sm:text-base hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                    Cancel
                                </button>
                            )}
                            <button 
                                type="submit"
                                disabled={loading}
                                className="w-full sm:flex-[2] px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-semibold text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all hover:shadow-lg"
                            >
                                {loading ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <Check className="h-5 w-5" />
                                        Create Community
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

CommunityCreator.propTypes = {
    onSuccess: PropTypes.func.isRequired,
    onCancel: PropTypes.func
};

export default CommunityCreator;
